/* eslint-disable max-lines */
import React, { useState, useEffect, useRef } from 'react'
import { Moment } from 'moment'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { Tabs } from '@mui/material'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import styled, { css } from 'styled-components'

import TabPanel from 'components/Tabs/TabPanel'

import AlertsPanel from 'pages/Device/AlertsPanel'
import Status from 'pages/Device/Status'
import DashboardStatus from 'pages/Device/SystemDashboard/Status'
import Notes from 'pages/Device/SystemDashboard/Notes'
import Reports from 'pages/Device/SystemDashboard/Reports'
import ConfigComponents from 'pages/Device/SystemDashboard/ConfigComponents'
import Alerts from 'pages/Device/SystemDashboard/Alerts'
import ThermostatRthPanel from 'pages/Device/ThermostatRthPanel'
import ConsumerRthPanel from 'pages/Device/ConsumerRthPanel'
import AwairRthPanel from 'pages/Device/AwairRthPanel'
import SystemConfigPanel from 'pages/Device/SystemConfigPanel'
import SystemComponentsPanel from 'pages/Device/SystemComponentsPanel'
import SystemReportsPanel from 'pages/Device/SystemReportsPanel'
import VerificationReportsPanel from 'pages/Device/VerificationReportsPanel'
import NotesPanel from 'pages/Device/NotesPanel'
import MaintenanceTrendsPanel from 'pages/Device/MaintenanceTrendsPanel'
import SystemDashboard from 'pages/Device/SystemDashboard'
import fallbackContent from 'pages/Device/fallbackContent'
import AssignedSystemTime from 'pages/Device/AssignedSystemTime'

import useDevice from 'hooks/useDevice'
import useSearchParams from 'hooks/useSearchParams'
import useTrackScrollPositionOnPage from 'hooks/useTrackScrollPositionOnPage'
import useTrackTimeOnPage from 'hooks/useTrackTimeOnPage'
import useHomeownerAlerts from 'hooks/useHomeownerAlerts'
import useVerificationReports from 'hooks/useVerificationReports'
import useSystemReports from 'hooks/useSystemReports'
import { useSessionContext } from 'hooks/SessionContext'
import { useStoreContext } from 'hooks/StoreContext'
import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'

import Brand from 'helpers/brand'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'
import KeyboardAccessible from 'components/KeyboardAccessible'

type Props = {
  dealerId: number
  deviceId: string
  isActive: boolean
  isUnassignedDevice?: boolean
  refreshPageCallback?: () => void | null
  className?: string
}

const TABS = {
  DASHBOARD: 'dashboard',
  STATUS: 'status',
  THERMOSTAT_RTH: 'thermostat-rth',
  MAINTENANCE_TRENDS: 'maintenance-trends',
  CUSTOMER_RUNTIME: 'customer-runtime',
  CONFIG_COMPONENTS: 'config-components',
  ALERTS: 'alerts',
  NOTES: 'notes',
  REPORTS: 'reports',
  AWAIR_RTH: 'awair-rth'
}

const Device = ({ dealerId, deviceId, isActive, isUnassignedDevice = false, refreshPageCallback }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { reportCache } = useStoreContext()
  const { session } = useSessionContext()
  const awairRthDate = useSearchParams('awairRthDate')
  const params = useParams()
  const { activeTab } = params

  const { enabledFeatures } = useFeaturesContext()
  const { hasAccess, published } = usePremiumFeaturesContext()

  useHomeownerAlerts(deviceId)
  useVerificationReports(deviceId)
  useSystemReports(deviceId)

  const { device, isDeviceLoaded } = useDevice(dealerId, deviceId)
  const [thermostatRthDate, setThermostatRthDate] = useState(useSearchParams('thermostatRthDate'))
  const [selectedTab, setSelectedTab] = useState(activeTab || 'dashboard')

  const rthRef = useRef<HTMLDivElement>(null)
  const brandName = Brand.brandName()

  const handleTabNavigation = (selectedTab: string) => {
    const currentLocation = location.pathname
    const newLocation = !activeTab && activeTab !== selectedTab ? currentLocation : currentLocation.replace(activeTab, selectedTab)

    setSelectedTab(selectedTab)

    navigate(newLocation)
  }

  useEffect(() => {
    if (!activeTab) {
      if (location.pathname === '/devices') {
        navigate(location.pathname + '/' + deviceId + '/dashboard')
      } else {
        navigate(location.pathname + 'dashboard')
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // TODO: This needs to be part of the refactor. We should not be adjusting state during render. Gus is pointing
  // to this stack overflow post: https://stackoverflow.com/questions/35290245/calling-setstate-in-render-is-not-avoidable
  const [noZones, setNoZones] = React.useState(false)
  const handleNoZonesStatus = () => {
    setNoZones(true)
  }

  const earlyReturnJsx = fallbackContent(isDeviceLoaded, device, refreshPageCallback, brandName)

  useTrackTimeOnPage('device_details', [60, 90, 120, 300], !earlyReturnJsx)
  useTrackScrollPositionOnPage('device_details', [50, 75], !earlyReturnJsx)

  if (earlyReturnJsx) {
    return earlyReturnJsx
  }

  if (device) {
    const hasAlertsCapability = device.capabilities.includes('alarms')
    const hasCurrentStatusCapability = device.capabilities.includes('current_status')
    const hasRthCapability = device.capabilities.includes('runtime_history')
    const hasSysConfigCapability = device.capabilities.includes('sys_config')
    const hasCommunicatingComponentsCapability = device.capabilities.includes('communicating_components')
    const hasZoneStatus = device.zoningEnabled
    const hasMaintenanceTrends = enabledFeatures.includes('paywall') && device.hasMaintenanceTrends
    const showVerificationReportsPanel = device.deviceType === 'control_hub'
    const showSystemReports = enabledFeatures.includes('system_reports') && published('system_reports_history')

    const showDayRthCallback = (time: Moment) => {
      rthRef.current?.scrollIntoView() // navigates from alerts panel
      setThermostatRthDate(time.format('MM-DD-YYYY'))
      setSelectedTab('thermostat-rth')

      navigate({
        pathname: activeTab ? location.pathname.replace(activeTab, 'thermostat-rth') : location.pathname + '/thermostat-rth',
        search: `?thermostatRthDate=${time.format('MM-DD-YYYY')}`
      })
    }

    const showDashboard = selectedTab === TABS.DASHBOARD

    const tabs: { enabled: boolean, label: string, link: string, icon?: string, toolTipText?: string,
      details: React.ReactNode | null, premiumTabName? : string, premiumDefaultHover? : string }[] = []

    if (hasRthCapability) {
      tabs.push({
        label: 'Thermostat Runtime',
        link: TABS.THERMOSTAT_RTH,
        details: (
          <ThermostatRthPanel
            isActive={isActive}
            device={device}
            timeZone={device.timeZone}
            thermostatRthDate={thermostatRthDate}
            ref={rthRef}
            updateThermostatRthDateCallback={setThermostatRthDate}
          />
        ),
        icon: 'rtgraph.png',
        toolTipText: 'Click to see Thermostat Runtime History Graph.',
        enabled: true

      })
    }

    if (hasMaintenanceTrends && published('maintenance_trends')) {
      tabs.push({
        premiumTabName: 'maintenance_trends',
        premiumDefaultHover: 'Click to see Maintenance Trends.',
        label: 'Maintenance Trends',
        link: TABS.MAINTENANCE_TRENDS,
        details: (
          <MaintenanceTrendsPanel dealerId={dealerId} device={device} />
        ),
        icon: 'trends.png',
        enabled: hasAccess('maintenance_trends')
      })
    }

    if (device.consumerRth) {
      tabs.push({
        label: 'Customer Runtime',
        link: TABS.CUSTOMER_RUNTIME,
        details: (
          <ConsumerRthPanel device={device} />
        ),
        icon: 'crtgraph.png',
        toolTipText: 'Click to see Customer Runtime History Graph.',
        enabled: true
      })
    }

    if (hasZoneStatus || !noZones) {
      tabs.push({
        label: showDashboard ? 'Status' : 'Status/Zones',
        link: TABS.STATUS,
        enabled: true,
        toolTipText: 'Click to see more Status/Zones details.',
        details: (
          showDashboard ? (
            <DashboardStatus
              deviceId={deviceId}
              hasAwairDevice={device.hasAwairDevice}
              reportCache={reportCache}
              onNoZones={handleNoZonesStatus}
            />
          ) : (
            <Status
              deviceId={deviceId}
              hasAwairDevice={device.hasAwairDevice}
              reportCache={reportCache}
            />
          )
        )
      })
    } else if (hasCurrentStatusCapability) {
      tabs.push({
        label: showDashboard ? 'Status' : 'System Status',
        link: TABS.STATUS,
        enabled: true,
        toolTipText: 'Click to see more System Status details.',
        details: (
          showDashboard ? (
            <DashboardStatus
              deviceId={deviceId}
              hasAwairDevice={device.hasAwairDevice}
              reportCache={reportCache}
              onNoZones={handleNoZonesStatus}
            />
          ) : (
            <Status
              deviceId={deviceId}
              hasAwairDevice={device.hasAwairDevice}
              reportCache={reportCache}
            />
          )
        )
      })
    }

    if (hasSysConfigCapability || hasCommunicatingComponentsCapability) {
      tabs.push({
        label: 'config/components',
        link: TABS.CONFIG_COMPONENTS,
        enabled: true,
        toolTipText: 'Click to see more System Configuration and Component details.',
        details: (showDashboard
          ? <ConfigComponents deviceId={deviceId} reportCache={reportCache} />
          : (
            <>
              <SystemConfigPanel
                deviceId={deviceId}
                deviceType={device.deviceType}
                reportCache={reportCache}
                readOnly={session.isFsr || session.isTechnician || isUnassignedDevice}
              />
              <SystemComponentsPanel
                deviceId={deviceId}
                communicating={device.communicating}
                reportCache={reportCache}
                readOnly={session.isFsr || session.isTechnician || isUnassignedDevice}
              />
            </>
          )
        )
      })
    }

    if (device.hasAwairDevice) {
      tabs.push({
        label: 'Awair Runtime History',
        link: TABS.AWAIR_RTH,
        details: (
          <AwairRthPanel
            deviceId={deviceId}
            timeZone={device.timeZone}
            datesWithData={device.awairDataPresence ?? []}
            awairDevices={device.awairDevices}
            awairRthDate={awairRthDate}
          />
        ),
        icon: 'awair.png',
        toolTipText: 'Click to see Awair Runtime History Graph.',
        enabled: true
      })
    }

    if (hasAlertsCapability) {
      tabs.push({
        label: 'Alerts',
        icon: 'alerts.png',
        link: TABS.ALERTS,
        enabled: true,
        toolTipText: 'Click to see Active Alerts and Alert History.',
        details: showDashboard
          ? <Alerts
            deviceId={deviceId}
            reportCache={reportCache}
            />
          : <AlertsPanel
            dealerId={dealerId}
            deviceId={deviceId}
            reportCache={reportCache}
            deviceTimeZone={device.timeZone}
            showDayRthCallback={showDayRthCallback}
            />
      })
    }

    if (showVerificationReportsPanel || showSystemReports) {
      tabs.push({
        label: 'Reports',
        link: TABS.REPORTS,
        enabled: true,
        details: (
          showDashboard
            ? <Reports showSystemReports={showSystemReports} showVerificationReports={showVerificationReportsPanel} />
            : (
              <>
                { showSystemReports && <SystemReportsPanel /> }
                { showVerificationReportsPanel && <VerificationReportsPanel /> }
              </>
            )
        )
      })
    }

    if (!session.isFsr) {
      tabs.push({
        label: 'Notes',
        link: TABS.NOTES,
        enabled: true,
        toolTipText: 'Click to view or edit Notes for the system.',
        details: (
          showDashboard
            ? <Notes notes={device.note} />
            : <NotesPanel device={device} refreshPageCallback={refreshPageCallback} />
        )
      })
    }

    const getTabIndex = (tab: string) => {
      if (tab === TABS.DASHBOARD) {
        return 0
      }

      const filteredTabs = tabs.filter(item => item.enabled)
      const index = filteredTabs.findIndex(item => item.link === tab)

      return index !== -1 ? index + 1 : 0
    }

    return (
      <S.Container isDashboard={showDashboard}>
        {showDashboard
          ? <SystemDashboard
              tabs={tabs}
              navigationCallback={handleTabNavigation}
            />
          : (
            <>
              <S.Tabs
                variant="scrollable"
                value={getTabIndex(selectedTab)}
                aria-label="device tabs"
                scrollButtons="auto"
                allowScrollButtonsMobile
                TabIndicatorProps={{ sx: { display: 'flex' } }}
              >
                <S.TabButton
                  onClick={() => handleTabNavigation(TABS.DASHBOARD)}
                  isActive={selectedTab === TABS.DASHBOARD}
                >
                  <DashboardOutlinedIcon />
                  <S.TabLabel isActive={selectedTab === TABS.DASHBOARD} >{'Dashboard'}</S.TabLabel>
                </S.TabButton>
                {tabs.map((tab) => (
                  tab.enabled && (
                    <S.TabButton
                      key={tab.link}
                      onClick={() => handleTabNavigation(tab.link)}
                      isActive={selectedTab === tab.link}
                    >
                      <S.TabLabel isActive={selectedTab === tab.link} >{tab.label}</S.TabLabel>
                    </S.TabButton>
                  )
                ))}
              </S.Tabs>
              {tabs.map((item) => (
                item.enabled && (
                  <TabPanel key={item.link} hidden={selectedTab !== item.link} id={item.link} ariaLabelledby={item.link}>
                    {item.details}
                  </TabPanel>
                )
              ))}
            </>
          )
        }
        <AssignedSystemTime device={device} refreshPageCallback={refreshPageCallback} />
      </S.Container>
    )
  }
}

export default Device

const S = {
  Container: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isDashboard'].includes(prop)
  })<{isDashboard: boolean}>`
    position: relative;
    display: block;
    padding: ${({ isDashboard }) => isDashboard ? '10px' : '0px'};
    background-color: ${({ isDashboard }) => isDashboard ? color('dashboardBackground') : color('paneBackground')};
    margin: 0px 10px 10px;
    @media ${screenSize.medium} {
      padding: 0;
      margin: 0;
    }
  `,
  Tabs: styled(Tabs)`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    flex-direction: row;
    position: sticky;
    z-index: 99;
    top: 0;
    background-color: ${color('dashboardBackground')};
    align-items: center;
    padding: 0.5rem 1rem;
    @media ${screenSize.medium} {
      flex-wrap: nowrap;
      padding: 0.5rem 0rem;
    }
    & .MuiTabs-flexContainer {
      display: flex;
      flex-wrap: wrap;
      width: 100%;
      flex-direction: row;
      justify-content: flex-start;
      gap: 1.5rem;
      @media ${screenSize.medium} {
        flex-wrap: nowrap;
      }
    }
    & .MuiTabs-scrollButtons svg{
      font-size: 2.5rem;
    }
  `,
  TabButton: styled(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['isActive'].includes(prop)
  })<{ isActive: boolean}>`
    position: relative;
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ isActive }) => (isActive ? color('activeTabHighlight') : color('modalOverlay'))};
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
    transition: border-bottom 0.4s ease-in-out;
    border-bottom: ${({ isActive }) => `4px solid ${color(isActive ? 'activeTabHighlight' : 'dashboardBackground')}`};
  `,
  TabLabel: styled.span.withConfig({
    shouldForwardProp: (prop) => !['isActive'].includes(prop)
  })<{ isActive:boolean }>`
    position: relative;
    display: inline-block;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    font-weight: ${({ isActive }) => (isActive ? '500' : '600')};
    text-transform: capitalize;
    min-height: 30px;
    line-height: 30px;

  ${props =>
    props.isActive &&
    css`
      &::after {
        width: 100%;
      }
    `}
`
}
